import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';

/**
 * Async thunk to fetch broker research calls for SME IPOs
 * GET /api/core/ipos/sme/reviews
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchSmeReviews = createAsyncThunk(
  'smeReviews/fetchSmeReviews',
  async (_options, { rejectWithValue }) => {
    try {
      const data = await ipoService.getSmeReviews();
      if (data?.success === false) {
        return rejectWithValue(data.error || 'Failed to fetch SME IPO reviews');
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch SME IPO reviews');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().smeReviews;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
        return false;
      }
      return true;
    },
  }
);

const initialState = {
  // Raw items from the API; the page maps them with utils/smeReviews.js mapSmeReviewItems
  items: [],
  total: 0,
  refreshedAt: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const smeReviewsSlice = createSlice({
  name: 'smeReviews',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSmeReviews.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSmeReviews.fulfilled, (state, action) => {
        const data = action.payload?.data || {};
        state.status = 'succeeded';
        state.items = Array.isArray(data.items) ? data.items : [];
        state.total = typeof data.total === 'number' ? data.total : state.items.length;
        state.refreshedAt = data.refreshed_at || null;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchSmeReviews.rejected, (state, action) => {
        // Keep previously loaded reviews so a failed refresh doesn't blank the page
        state.status = 'failed';
        state.error = action.payload || 'Failed to load SME IPO reviews';
      });
  },
});

export default smeReviewsSlice.reducer;
